import { memo, useContext, useMemo } from 'react';
import styled, { type CSSObject } from '@emotion/styled';

import { css, type ThemeProps } from '@emotion/react';

import { cleanUserInput } from '../../../lib/cleanUserInput';
import { colorAlpha } from '../../../lib/colorAlpha';
import getEventDateTime, {
  type EventDateTimeParams,
} from '../../../lib/getEventDateTime';
import { useIsLiteSite } from '../../../lib/useIsLiteSite';
import { themeFont } from '../../../lib/themeFont';
import { WWContext, type WWContextValue } from '../../WWContext';
import EventTime from './EventTime';
import ScheduleSubEvent from './ScheduleSubEvent';
import { variables } from '@xo-union/tk-ui-breakpoints';

import {
  isWwsWeddingWebsiteV2,
  type Maybe,
  type Gds_Event,
  type Gds_SubEvent,
} from '../../../types/graphql';

const DesktopEventWrap = () => css`
  padding: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  .verticalLayout.homePage & {
    margin-right: 2rem;
    margin-top: 24px;
    align-items: start;
  }
`;

const MobileEventWrap = () => css`
  padding: 0 20px;
`;

const EventWrap = styled('div')`
  margin: 0;
  width: 100%;

  @media (${variables.inMobile}) {
    ${MobileEventWrap}
  }

  @media (${variables.inDesktop}) {
    ${DesktopEventWrap}
  }

  ${(props: ThemeProps): CSSObject | undefined => props.theme.components.Event}
`;

const DesktopDetailsWrap = () => css`
  .verticalLayout.homePage & {
    align-items: flex-start;
    margin: 0 0 24px;
    text-align: left;
  }
`;

const DetailsWrap = styled('div')`
  display: flex;
  flex-direction: column;
  text-align: center;
  margin: 48px 0 24px;
  align-items: center;

  @media (${variables.inDesktop}) {
    ${DesktopDetailsWrap}
  }
`;

const SubEventsWrap = styled('div')`
  ${(props: ThemeProps): string =>
    `border-top: 1px solid ${colorAlpha(props.theme.fonts.s6.color, '15%')};`}
`;

const DesktopName = () => css`
  .verticalLayout.homePage & {
    margin: 0 0 8px 0;
  }
`;

const Name = styled('h4')`
  line-height: 1;
  margin: 32px 0 8px 0;

  @media (${variables.inDesktop}) {
    ${DesktopName}
  }

  ${themeFont('s3')}
`;

const Grouping = styled('div')`
  margin: 8px 0;
`;

const Detail = styled('div')`
  margin: 4px 0;

  ${themeFont('s6')}
`;

const DateTime = styled('div')`
  margin: 8px 0;

  ${themeFont('s4')}
`;
const Description = styled('div')`
  margin: 8px 0;
  max-width: 460px;
  white-space: pre-line;

  ${themeFont('s6')}
`;

export interface ScheduleEventProps {
  event: Gds_Event;
  row: number;
}

function ScheduleEvent({ event, row }: ScheduleEventProps) {
  const {
    id,
    date,
    notes,
    endTime,
    location,
    name,
    subEvents,
    time,
    visible,
    attire,
    type,
  } = event;

  const { data } = useContext<WWContextValue>(WWContext);

  const isLiteSite = useIsLiteSite();

  const dangerouslySetInnerHTML = useMemo<{
    __html: string;
  }>(() => {
    return { __html: cleanUserInput(notes) };
  }, [notes]);

  // Show subEvents only if they are visible and have at least one piece of user-added data
  const visibleSubEvents = useMemo<Gds_SubEvent[] | undefined>(() => {
    const additionalInfoFields: Array<keyof Gds_SubEvent> = [
      'attire',
      'endTime',
      'location',
      'notes',
      'time',
    ];

    return subEvents
      ?.filter((e: Gds_SubEvent): boolean => Boolean(e.visible))
      .filter((subEvent: Gds_SubEvent) =>
        additionalInfoFields.some((field: keyof Gds_SubEvent): boolean =>
          Boolean(subEvent[field])
        )
      );
  }, [subEvents]);

  const requiredProps = [name];

  const requiredPropsMissing = requiredProps.some(
    (p: Maybe<string> | undefined): boolean => !p
  );

  if (requiredPropsMissing || !visible) {
    return null;
  }

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const isWeddingDayEvent = type === 'WeddingDay';

  const wordyFormat =
    isWeddingDayEvent &&
    ['ornate', 'verbose'].includes(
      data?.weddingWebsiteResult.weddingDateFormat || ''
    );

  const params: EventDateTimeParams = {
    date,
    endTime,
    isWeddingDayEvent,
    time,
    weddingDateFormat: data?.weddingWebsiteResult.weddingDateFormat,
    isLiteSite,
  };

  const {
    date: formattedDate,
    time: formattedTime,
    endTime: formattedEndTime,
  } = getEventDateTime(params);

  return (
    <EventWrap data-testid={`event-row-${row}`}>
      <DetailsWrap data-testid={`event-${id}`}>
        <Grouping>
          <Name>{name}</Name>

          {isWeddingDayEvent && data?.weddingWebsiteResult.hideDate ? (
            <Detail>Date To Be Announced</Detail>
          ) : (
            <>
              {formattedDate && <DateTime>{formattedDate}</DateTime>}

              {!wordyFormat && (
                <DateTime>
                  <EventTime time={formattedTime} endTime={formattedEndTime} />
                </DateTime>
              )}
            </>
          )}
        </Grouping>

        {(location?.name || location?.fullAddress) && (
          <Grouping>
            {location?.name && <Detail>{location.name}</Detail>}

            {location?.fullAddress && <Detail>{location.fullAddress}</Detail>}
          </Grouping>
        )}

        {attire && <Detail>Attire: {attire}</Detail>}

        {notes && (
          <Grouping>
            <Description dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
          </Grouping>
        )}
      </DetailsWrap>

      {!!visibleSubEvents?.length && (
        <SubEventsWrap>
          {visibleSubEvents.map((subEvent: Gds_SubEvent) => (
            <ScheduleSubEvent {...subEvent} key={subEvent.id} />
          ))}
        </SubEventsWrap>
      )}
    </EventWrap>
  );
}

export default memo(ScheduleEvent);
